import { useState, useEffect } from 'react';
import api, { getAllHistory } from '../utils/api';

const TX_TYPES = [
  { value: 'all',          label: 'All Transactions' },
  { value: 'received',     label: 'Received' },
  { value: 'sent',         label: 'Sent' },
  { value: 'deposit',      label: 'Deposits' },
  { value: 'boost_reward', label: 'Boost Rewards' },
  { value: 'cashout',      label: 'Cashouts' },
];

const TYPE_CONFIG = {
  deposit:          { color: '#00C48C', sign: '+', bg: '#E6FAF5', label: 'Added funds' },
  send:             { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Sent' },
  receive:          { color: '#00C48C', sign: '+', bg: '#E6FAF5', label: 'Received' },
  cashout_spend:    { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Cashed out' },
  cashout_save:     { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Cashed out Earning' },
  cashout:          { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Cashed out' },
  payment:          { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Payment sent' },
  payment_sent:     { color: '#FF3B30', sign: '-', bg: '#FFF0EF', label: 'Payment sent' },
  payment_received: { color: '#00C48C', sign: '+', bg: '#E6FAF5', label: 'Payment received' },
  spend_to_stack:   { color: '#007AFF', sign: '↓', bg: '#EBF4FF', label: 'Moved to Earning' },
  stack_to_spend:   { color: '#007AFF', sign: '↑', bg: '#EBF4FF', label: 'Moved to Spend' },
  spend_to_save:    { color: '#007AFF', sign: '↓', bg: '#EBF4FF', label: 'Moved to Earning' },
  save_to_spend:    { color: '#007AFF', sign: '↑', bg: '#EBF4FF', label: 'Moved to Spend' },
  boost_reward:     { color: '#FF9500', sign: '⚡', bg: '#FFF5E6', label: 'Boost reward' },
};

function formatDate(ts) {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function formatMethod(tx) {
  if (tx.metadata?.source === 'wave') return 'Wave Invoice';
  if (tx.txHash?.startsWith('pi_')) return 'Card';
  if (tx.txHash?.startsWith('0x')) return 'On-chain';
  if (tx.counterparty) return 'Thunderbolt';
  return '—';
}

function exportCSV(txs) {
  let totalIn = 0, totalOut = 0, totalFees = 0;
  const rows = txs.map(tx => {
    const cfg    = TYPE_CONFIG[tx.type] || { label: tx.type, sign: '' };
    const amount = parseFloat(tx.amountUsd || tx.netUsd || 0);
    const fee    = parseFloat((tx.feeCents || 0) / 100);
    const net    = parseFloat(tx.netUsd || tx.amountUsd || 0);
    const isIn   = ['payment_received','deposit','receive'].includes(tx.type);
    const isOut  = ['send','cashout_spend','cashout_save','payment','payment_sent'].includes(tx.type);
    if (isIn)  totalIn  += amount;
    if (isOut) totalOut += amount;
    totalFees += fee;
    return [
      new Date(tx.createdAt * 1000).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }),
      new Date(tx.createdAt * 1000).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' }),
      cfg.label,
      isIn  ? amount.toFixed(2) : '',
      isOut ? amount.toFixed(2) : '',
      fee > 0 ? fee.toFixed(2) : '',
      net.toFixed(2),
      formatMethod(tx),
      tx.metadata?.waveInvoiceNumber ? 'Wave #' + tx.metadata.waveInvoiceNumber : '',
      tx.id,
    ].map(v => `"${v}"`).join(',');
  });
  const net = totalIn - totalOut;
  const header  = ['Date','Time','Description','Money In ($)','Money Out ($)','Fee ($)','Net ($)','Method','Reference','Transaction ID'];
  const summary = [
    '',
    '"--- SUMMARY ---"',
    `"Total Money In","${totalIn.toFixed(2)}"`,
    `"Total Money Out","${totalOut.toFixed(2)}"`,
    `"Total Fees Paid","${totalFees.toFixed(2)}"`,
    `"Net P&L","${net.toFixed(2)}"`,
    `"Transactions","${txs.length}"`,
  ];
  const csv  = [header.map(h => `"${h}"`).join(','), ...rows, ...summary].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `thunderbolt-transactions-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZE = 50;

export default function TransactionsPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Transactions';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const [txs, setTxs]           = useState([]);
  const [pending, setPending]   = useState([]);
  const [tab, setTab]           = useState('activity');
  const [filter, setFilter]     = useState('all');
  const [contactFilter, setContactFilter] = useState('all');
  const [contacts, setContacts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [page, setPage]         = useState(1);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [allTxs, pendRes] = await Promise.all([
        getAllHistory(),
        api.get('/payments/pending-requests'),
      ]);
      const sorted = allTxs.sort((a, b) => b.createdAt - a.createdAt);
      setTxs(sorted);
      setPending(pendRes.data || []);
      // Extract unique contacts from transactions
      const seen = {};
      const contactList = [];
      for (const tx of sorted) {
        if (tx.contactName && !seen[tx.contactName]) {
          seen[tx.contactName] = true;
          contactList.push({ name: tx.contactName, email: tx.contactEmail });
        }
      }
      setContacts(contactList);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this payment request?')) return;
    setDeleting(id);
    try {
      await api.delete(`/payments/request/${id}`);
      setPending(prev => prev.filter(p => p.id !== id));
      setExpanded(null);
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete request'); }
    setDeleting(null);
  }

  const filtered = txs.filter(t => {
    if (contactFilter !== 'all' && t.contactName !== contactFilter) return false;
    if (filter === 'all') return true;
    if (filter === 'received') return t.type === 'receive' || t.type === 'payment_received';
    if (filter === 'sent')     return t.type === 'send' || t.type === 'payment' || t.type === 'payment_sent';
    if (filter === 'cashout')  return t.type === 'cashout_spend' || t.type === 'cashout_save' || t.type === 'cashout';
    return t.type === filter;
  });

  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const summary = txs.reduce((acc, tx) => {
    const amount = parseFloat(tx.amountUsd || tx.netUsd || 0);
    const fee    = parseFloat((tx.feeCents || 0) / 100);
    if (['payment_received','deposit','receive'].includes(tx.type)) acc.totalIn += amount;
    if (['send','cashout_spend','cashout_save','payment','payment_sent'].includes(tx.type)) acc.totalOut += amount;
    acc.totalFees += fee;
    acc.count++;
    return acc;
  }, { totalIn: 0, totalOut: 0, totalFees: 0, count: 0 });

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleRow}>
          <div style={s.title}>Transactions</div>
          <button style={s.exportBtn} onClick={() => exportCSV(filtered)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      <div style={s.summaryCard}>
        <div style={s.summaryItem}>
          <div style={s.summaryLabel}>Money In</div>
          <div style={{...s.summaryValue, color:'#00C48C'}}>${summary.totalIn.toFixed(2)}</div>
        </div>
        <div style={s.summaryDivider}/>
        <div style={s.summaryItem}>
          <div style={s.summaryLabel}>Money Out</div>
          <div style={{...s.summaryValue, color:'#FF3B30'}}>${summary.totalOut.toFixed(2)}</div>
        </div>
        <div style={s.summaryDivider}/>
        <div style={s.summaryItem}>
          <div style={s.summaryLabel}>Fees Paid</div>
          <div style={{...s.summaryValue, color:'#6E6E73'}}>${summary.totalFees.toFixed(2)}</div>
        </div>
        <div style={s.summaryDivider}/>
        <div style={s.summaryItem}>
          <div style={s.summaryLabel}>Transactions</div>
          <div style={{...s.summaryValue, color:'#1D1D1F'}}>{summary.count}</div>
        </div>
      </div>

      <div style={s.segmentWrap}>
        <div style={s.segment}>
          <button style={{...s.segBtn, ...(tab==='activity' ? s.segBtnActive : {})}} onClick={() => setTab('activity')}>
            All Transactions
          </button>
          <button style={{...s.segBtn, ...(tab==='pending' ? s.segBtnActive : {})}} onClick={() => setTab('pending')}>
            Pending Requests
            {pending.length > 0 && (
              <span style={{...s.badge, ...(tab==='pending' ? s.badgeActive : {})}}>{pending.length}</span>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={s.loadingWrap}>
          <div style={s.skeleton}/>
          <div style={{...s.skeleton, width:'70%', marginTop:8}}/>
          <div style={{...s.skeleton, width:'85%', marginTop:8}}/>
        </div>
      ) : (
        <>
          {tab === 'activity' && (
            <div style={s.section}>
              <div style={s.dropRow}>
                <div style={{position:'relative'}}>
                  <button style={s.dropWrap} onClick={() => setDropOpen(o => !o)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2.5" style={{flexShrink:0}}>
                      <path d="M4 6h16M7 12h10M10 18h4"/>
                    </svg>
                    <span style={s.dropLabel}>{TX_TYPES.find(t => t.value === filter)?.label}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="2.5" style={{flexShrink:0, transform: dropOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s'}}>
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {dropOpen && (
                    <>
                      <div style={s.dropOverlay} onClick={() => setDropOpen(false)}/>
                      <div style={s.dropMenu}>
                        {TX_TYPES.map((t, i) => (
                          <button key={t.value}
                            style={{...s.dropItem, ...(filter===t.value ? s.dropItemActive : {}), borderBottom: i < TX_TYPES.length-1 ? '1px solid #F5F5F7' : 'none'}}
                            onClick={() => { setFilter(t.value); setExpanded(null); setDropOpen(false); setPage(1); }}>
                            {t.label}
                            {filter===t.value && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2.5">
                                <path d="M20 6L9 17l-5-5"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                {contacts.length > 0 && (
                  <select
                    style={{...s.contactSelect}}
                    value={contactFilter}
                    onChange={e => { setContactFilter(e.target.value); setPage(1); setExpanded(null); }}>
                    <option value="all">All contacts</option>
                    {contacts.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                )}
                <span style={s.countPill}>{filtered.length} transactions</span>
              </div>

              {filtered.length === 0 ? (
                <div style={s.empty}>
                  <div style={s.emptyIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="1.5">
                      <rect x="2" y="5" width="20" height="14" rx="3"/>
                      <path d="M2 10h20"/>
                    </svg>
                  </div>
                  <div style={s.emptyText}>No transactions found</div>
                  <div style={s.emptySub}>Nothing here for this filter</div>
                </div>
              ) : (
                <div>
                  {filtered.length > PAGE_SIZE && (
                    <div style={s.pageInfo}>
                      Showing {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, filtered.length)} of {filtered.length}
                    </div>
                  )}
                  <div style={s.txList}>
                    {paginated.map(tx => {
                      const cfg   = TYPE_CONFIG[tx.type] || { label: tx.type, color: '#6E6E73', sign: '', bg: '#F5F5F7' };
                      const isExp = expanded === tx.id;
                      return (
                        <div key={tx.id}>
                          <div style={s.txItem} onClick={() => setExpanded(isExp ? null : tx.id)}>
                            <div style={{...s.txDot, background: cfg.bg, color: cfg.color}}>{cfg.sign}</div>
                            <div style={s.txInfo}>
                              <div style={s.txLabel}>
                                {tx.metadata?.source === 'wave' ? 'Wave Invoice #' + tx.metadata.waveInvoiceNumber : cfg.label}
                                {tx.contactName ? ' — ' + tx.contactName : ''}
                              </div>
                              <div style={s.txDate}>{formatDate(tx.createdAt)} · {formatMethod(tx)}</div>
                            </div>
                            <div style={{...s.txAmount, color: cfg.color}}>{cfg.sign}${tx.amountUsd ?? tx.netUsd}</div>
                          </div>
                          {isExp && (
                            <div style={s.detail}>
                              <div style={s.detailRow}><span style={s.detailLabel}>Type</span><span style={s.detailValue}>{cfg.label}</span></div>
                              <div style={s.detailRow}><span style={s.detailLabel}>Amount</span><span style={s.detailValue}>${tx.amountUsd}</span></div>
                              {tx.feeCents > 0 && <div style={s.detailRow}><span style={s.detailLabel}>Fee</span><span style={s.detailValue}>${(tx.feeCents/100).toFixed(2)}</span></div>}
                              {tx.netCents && tx.feeCents > 0 && <div style={s.detailRow}><span style={s.detailLabel}>Net</span><span style={s.detailValue}>${tx.netUsd}</span></div>}
                              <div style={s.detailRow}><span style={s.detailLabel}>Method</span><span style={s.detailValue}>{formatMethod(tx)}</span></div>
                              {tx.counterparty && <div style={s.detailRow}><span style={s.detailLabel}>{tx.type==='payment_sent'?'To':'From'}</span><span style={s.detailValue}>{tx.counterparty}</span></div>}
                              {tx.metadata?.waveInvoiceNumber && <div style={s.detailRow}><span style={s.detailLabel}>Invoice #</span><span style={s.detailValue}>{tx.metadata.waveInvoiceNumber}</span></div>}
                              {tx.txHash && <div style={s.detailRow}><span style={s.detailLabel}>TX ID</span><span style={{...s.detailValue, fontFamily:'monospace', fontSize:11, cursor:'pointer'}} onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(tx.txHash); }}>{tx.txHash} <span style={{color:'#007AFF', fontSize:10}}>⧉</span></span></div>}
                              <div style={s.detailRow}><span style={s.detailLabel}>Date</span><span style={s.detailValue}>{formatDate(tx.createdAt)}</span></div>
                              <div style={s.detailRow}><span style={s.detailLabel}>ID</span><span style={{...s.detailValue, fontFamily:'monospace', fontSize:11, cursor:'pointer'}} onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(tx.id); }}>{tx.id} <span style={{color:'#007AFF', fontSize:10}}>⧉</span></span></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div style={s.pageRow}>
                      <button style={{...s.pageBtn, opacity: page===1 ? 0.3 : 1}} disabled={page===1} onClick={() => { setPage(p => p-1); setExpanded(null); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M15 18l-6-6 6-6"/>
                        </svg>
                        Prev
                      </button>
                      <span style={s.pageCount}>Page {page} of {totalPages}</span>
                      <button style={{...s.pageBtn, opacity: page>=totalPages ? 0.3 : 1}} disabled={page>=totalPages} onClick={() => { setPage(p => p+1); setExpanded(null); }}>
                        Next
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === 'pending' && (
            <div style={s.section}>
              {pending.length === 0 ? (
                <div style={s.empty}>
                  <div style={s.emptyIcon}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="1.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div style={s.emptyText}>No pending requests</div>
                  <div style={s.emptySub}>All caught up</div>
                </div>
              ) : (
                <div style={s.txList}>
                  {pending.map(p => (
                    <div key={p.id}>
                      <div style={s.txItem} onClick={() => setExpanded(expanded===p.id ? null : p.id)}>
                        <div style={{...s.txDot, background:'#FFF5E6', color:'#FF9500'}}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF9500" strokeWidth="2" strokeLinecap="round">
                            <circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/>
                          </svg>
                        </div>
                        <div style={s.txInfo}>
                          <div style={s.txLabel}>
                            {p.waveInvoiceNumber ? 'Wave Invoice #' + p.waveInvoiceNumber : p.description || 'Payment Request'}
                            {p.contactName ? ' — ' + p.contactName : ''}
                            <span style={s.pendingBadge}>UNPAID</span>
                          </div>
                          <div style={s.txDate}>{formatDate(p.createdAt)}</div>
                        </div>
                        <div style={{...s.txAmount, color:'#FF9500'}}>${p.amountUsd}</div>
                      </div>
                      {expanded===p.id && (
                        <div style={s.detail}>
                          <div style={s.detailRow}><span style={s.detailLabel}>Status</span><span style={{...s.detailValue, color:'#FF9500'}}>Awaiting Payment</span></div>
                          <div style={s.detailRow}><span style={s.detailLabel}>Amount</span><span style={s.detailValue}>${p.amountUsd}</span></div>
                          {p.contactName && <div style={s.detailRow}><span style={s.detailLabel}>Contact</span><span style={s.detailValue}>{p.contactName}</span></div>}
                          {p.contactEmail && <div style={s.detailRow}><span style={s.detailLabel}>Email</span><span style={s.detailValue}>{p.contactEmail}</span></div>}
                          {p.contactPhone && <div style={s.detailRow}><span style={s.detailLabel}>Phone</span><span style={s.detailValue}>{p.contactPhone}</span></div>}
                          <div style={s.detailRow}>
                            <span style={s.detailLabel}>Payment Link</span>
                            <div style={s.linkActions}>
                              <span style={s.linkUrl}>{(p.shortUrl || p.payUrl || p.paymentLink || '').replace(/^https?:\/\//, '')}</span>
                              <button style={s.copyBtn} onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(p.shortUrl || p.payUrl || p.paymentLink); }}>Copy</button>
                            </div>
                          </div>
                          <div style={s.detailRow}><span style={s.detailLabel}>Request ID</span><span style={{...s.detailValue, fontFamily:'monospace', fontSize:11, cursor:'pointer'}} onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(p.id); }}>{p.id} <span style={{color:'#007AFF', fontSize:10}}>⧉</span></span></div>
                          <button style={s.deleteBtn} disabled={deleting===p.id} onClick={e => { e.stopPropagation(); handleDelete(p.id); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
                            </svg>
                            {deleting===p.id ? 'Deleting…' : 'Delete Request'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s = {
  page:           { minHeight: '100vh', background: '#F5F5F7', paddingBottom: 88 },
  header:         { padding: '56px 20px 16px' },
  titleRow:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:          { fontSize: 28, fontWeight: 700, letterSpacing: -0.5, color: '#1D1D1F' },
  exportBtn:      { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 600, color: '#007AFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer' },
  summaryCard:    { display: 'flex', alignItems: 'center', margin: '0 16px 16px', background: '#fff', borderRadius: 20, padding: '16px 0', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  summaryItem:    { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  summaryDivider: { width: 1, background: '#F0F0F0', alignSelf: 'stretch', margin: '0 4px' },
  summaryLabel:   { fontSize: 11, color: '#AEAEB2', fontWeight: 500 },
  summaryValue:   { fontSize: 16, fontWeight: 700, letterSpacing: -0.3 },
  segmentWrap:    { padding: '0 16px 16px' },
  segment:        { display: 'flex', background: '#E5E5EA', borderRadius: 12, padding: 3, gap: 2 },
  segBtn:         { flex: 1, padding: '8px 12px', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, color: '#6E6E73', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  segBtnActive:   { background: '#fff', color: '#1D1D1F', fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  badge:          { background: '#AEAEB2', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 20, minWidth: 18, textAlign: 'center' },
  badgeActive:    { background: '#FF9500' },
  dropRow:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  dropWrap:       { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 14, padding: '11px 16px', boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer' },
  dropLabel:      { fontSize: 15, fontWeight: 600, color: '#1D1D1F', letterSpacing: -0.2, minWidth: 140, textAlign: 'left' },
  dropOverlay:    { position: 'fixed', inset: 0, zIndex: 99 },
  dropMenu:       { position: 'absolute', top: 'calc(100% + 8px)', left: 0, background: '#fff', borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,0.14)', zIndex: 100, minWidth: 220, overflow: 'hidden' },
  dropItem:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '14px 18px', background: 'none', border: 'none', fontSize: 15, color: '#1D1D1F', cursor: 'pointer', textAlign: 'left' },
  dropItemActive: { color: '#007AFF', fontWeight: 600 },
  countPill:      { fontSize: 12, color: '#AEAEB2', fontWeight: 500 },
  loadingWrap:    { padding: '24px 20px' },
  skeleton:       { height: 20, borderRadius: 8, width: '100%', background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '400px 100%', animation: 'shimmer 1.5s infinite' },
  section:        { margin: '0 16px 16px' },
  empty:          { background: '#fff', borderRadius: 20, padding: '40px 20px', textAlign: 'center', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  emptyIcon:      { marginBottom: 12, display: 'flex', justifyContent: 'center' },
  emptyText:      { fontSize: 15, fontWeight: 500, marginBottom: 4 },
  emptySub:       { fontSize: 13, color: '#6E6E73' },
  txList:         { background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  txItem:         { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #F5F5F7', cursor: 'pointer' },
  txDot:          { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  txInfo:         { flex: 1 },
  txLabel:        { fontSize: 14, fontWeight: 500, color: '#1D1D1F', display: 'flex', alignItems: 'center', gap: 6 },
  txDate:         { fontSize: 12, color: '#AEAEB2', marginTop: 2 },
  txAmount:       { fontSize: 14, fontWeight: 600 },
  pendingBadge:   { background: '#FFF5E6', color: '#FF9500', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 6 },
  detail:         { borderTop: '1px solid #F5F5F7', padding: '12px 16px', background: '#FAFAFA' },
  detailRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13, borderBottom: '1px solid #F0F0F0' },
  detailLabel:    { color: '#AEAEB2', flexShrink: 0 },
  detailValue:    { color: '#1D1D1F', fontWeight: 500, wordBreak: 'break-all', maxWidth: '60%', textAlign: 'right' },
  linkActions:    { display: 'flex', alignItems: 'center', gap: 8, maxWidth: '65%' },
  linkUrl:        { fontSize: 11, color: '#6E6E73', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  copyBtn:        { background: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  contactSelect:  { padding:'8px 12px', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:13, color:'#1D1D1F', outline:'none', cursor:'pointer' },
  pageInfo:       { fontSize: 12, color: '#AEAEB2', textAlign: 'center', marginBottom: 8 },
  pageRow:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 4px', marginTop: 8 },
  pageBtn:        { display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', background: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, color: '#007AFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer' },
  pageCount:      { fontSize: 13, color: '#6E6E73', fontWeight: 500 },
  deleteBtn:      { width: '100%', marginTop: 12, padding: '12px 0', background: '#FFF0EF', color: '#FF3B30', border: '1px solid #FFD0CE', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
};
